import type {SupabaseClient} from './supabaseTypes.js';
import type {FormRecord} from './offlineForms.js';
import {lastAuthenticatedUserId} from './authIdentityMemory.js';

export const QUEUED_REPORT_CONFLICT = 'WCF_REPLAY_CONFLICT';

export async function queuedReportActor(sb: SupabaseClient): Promise<string | null> {
  // Definitely offline (navigator.onLine is false only when there is no
  // network at all): getSession() would spend its whole refresh deadline,
  // about 25 s, failing to refresh an expired token before falling through to
  // the remembered account below. Use that account straight away.
  const known = lastAuthenticatedUserId();
  if (known && typeof navigator !== 'undefined' && navigator.onLine === false) return known;
  const {data, error} = await sb.auth.getSession();
  const live = data.session?.user.id;
  if (live) return live;
  if (error) {
    // Offline with an expired access token: the refresh failed but the device
    // is still signed in as the remembered account (authIdentityMemory.ts), so
    // the report can still be queued under it. No remembered account: surface
    // the real error rather than guessing.
    const remembered = lastAuthenticatedUserId();
    if (remembered) return remembered;
    throw error;
  }
  return null;
}

// The server checks the current principal again. A constraint name or an
// error-free but incomplete envelope is never a receipt for a saved report.
export async function submitQueuedReport(
  sb: SupabaseClient,
  formKind: string,
  record: FormRecord,
  actorProfileId: string,
) {
  const {data, error} = await sb.rpc('submit_queued_report', {
    p_form_kind: formKind,
    p_record: record,
    p_actor_profile_id: actorProfileId,
  });
  if (error) return {data, error};
  if (
    !data ||
    data.ok !== true ||
    data.verified !== true ||
    data.receipt_version !== 1 ||
    data.actor_profile_id !== actorProfileId ||
    data.form_kind !== formKind ||
    data.id !== record.id ||
    data.client_submission_id !== record.client_submission_id
  ) {
    return {
      data: null,
      error: {code: QUEUED_REPORT_CONFLICT, message: 'Could not verify this report was saved. Open it to review.'},
    };
  }
  return {data, error: null};
}
